import {ZadPurchase, ZadPurchaseItem, ZadPurchaseUnit} from '../../../models/zad'
import {ZadApiError} from './errors'
import {normalizeZadPurchaseUnit} from './packagingHelpers'
import {isZadProductArchived} from './productHelpers'
import {canShowZadSaleCostMargin} from './saleHelpers'

export {isZadProductArchived}

export type ZadPurchaseCancelBlocker = {
  code?: string
  messageEn?: string
  messageFr?: string
  productId?: string
  saleNumbers?: string[]
  returnNumbers?: string[]
  movements?: Array<{
    productId?: string
    type?: string
    movementId?: string
  }>
}

export type ZadPurchaseStockReversalRow = {
  productId: string
  productCode?: string | null
  productName?: string | null
  quantityBaseUnits: number
  currentStock: number
  projectedStock: number
  originalReceiptQuantity?: number
  reversibleQuantity?: number
  safeToReverseQuantity?: number
  deferredQuantity?: number
  projectedStockAfterForce?: number
  stockAttributionStatus?: string | null
  reconciliationReason?: string | null
  physicalReversalWillOccur?: boolean
  requiresConfirmUnverifiedStock?: boolean
  stockReconciliationStatus?: string | null
  purchaseQuantity?: number
  purchaseUnit?: ZadPurchaseUnit | string | null
  unitsPerPackageSnapshot?: number | null
}

export type ZadPurchaseForcePreview = {
  eligible?: boolean
  confirmRequired?: boolean
  requiresConfirmUnverifiedStock?: boolean
  administrativeCancellation?: boolean
  physicalStockReversal?: boolean
  blockers?: ZadPurchaseCancelBlocker[]
}

export type ZadPurchaseFinancialConsequences = {
  allocatedAmount?: number
  amountAlreadyPaid?: number
  outstandingAmount?: number
  amountRequiringReconciliation?: number
  automaticRefundCreated?: boolean
  paymentsRemainAllocated?: boolean
  paymentsAreNotDeleted?: boolean
  financialReconciliationStatus?: string | null
  allocations?: unknown
}

export type ZadPurchaseReconciliationRequirements = {
  stock?: string | null
  financial?: string | null
  cost?: string | null
}

export type ZadPurchaseReconciliationRow = {
  purchaseId: string
  purchaseNumber?: string
  status?: string
  supplier?: ZadPurchase['supplier']
  cancelledAt?: string | null
  cancelledBy?: unknown
  operationId?: string | null
  mode?: string | null
  confirmForce?: boolean
  confirmUnverifiedStock?: boolean
  stockReconciliationStatus?: string | null
  financialReconciliationStatus?: string | null
  costReconciliationStatus?: string | null
  reconciliation?: unknown
  reason?: string | null
  total?: number
}

export type ZadPurchaseArchiveCandidate = {
  productId: string
  productCode?: string | null
  productName?: string | null
  reasons?: string[]
}

export type ZadPurchaseCancellationPreview = {
  purchaseId: string
  purchaseNumber?: string
  status?: string
  purchaseDate?: string
  supplier?: ZadPurchase['supplier']
  lineCount?: number
  total?: number
  canCancel: boolean
  canForceCancel?: boolean
  force?: ZadPurchaseForcePreview
  stockReversal?: ZadPurchaseStockReversalRow[]
  financialConsequences?: ZadPurchaseFinancialConsequences
  reconciliationRequirements?: ZadPurchaseReconciliationRequirements
  linkedSupplierPayments?: number
  productsToArchive?: ZadPurchaseArchiveCandidate[]
  productsToKeep?: ZadPurchaseArchiveCandidate[]
  blockers?: ZadPurchaseCancelBlocker[]
  linkedSales?: string[]
  linkedReturns?: string[]
}

export function canAdministerZadArchives(role?: string | number | null): boolean {
  return canShowZadSaleCostMargin(role)
}

export function canCancelValidatedZadPurchase(
  status?: string | null,
  role?: string | number | null
): boolean {
  return status === 'VALIDATED' && canAdministerZadArchives(role)
}

export function canOfferZadForcePurchaseCancel(
  preview?: Pick<ZadPurchaseCancellationPreview, 'canCancel' | 'canForceCancel' | 'force'> | null,
  role?: string | number | null
): boolean {
  if (!canAdministerZadArchives(role)) return false
  if (!preview || preview.canCancel === true) return false
  if (preview.force?.eligible === false) return false
  return preview.canForceCancel === true || preview.force?.eligible === true
}

export function newZadPurchaseCancelOperationId(purchaseId: string): string {
  const token =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `ZAD-PURCHASE-CANCEL:${purchaseId}:${token}`
}

export function newZadPurchaseForceCancelOperationId(purchaseId: string): string {
  const token =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `ZAD-PURCHASE-FORCE-CANCEL:${purchaseId}:${token}`
}

export function isZadValidatedPurchaseCancelUnknown(
  err?: {errorCode?: string; status?: number} | null
): boolean {
  if (!err) return true
  if (err.errorCode === 'PURCHASE_NOT_CANCELLABLE' && err.status === 503) return false
  if (err.status === 404 || err.status === 405 || err.status === 409) return false
  if (err.status === 400 || err.status === 403) return false
  if (err.errorCode === 'NETWORK' || err.errorCode === 'ECONNABORTED') return true
  if (!err.errorCode && (err.status == null || err.status === 0)) return true
  return err.status === 408 || err.status === 502 || err.status === 504
}

export function zadPurchaseCancelBlockerMessageId(code?: string | null): string {
  const map: Record<string, string> = {
    PURCHASE_HAS_LINKED_SALES: 'ZAD.PURCHASES.CANCEL_BLOCK_SALES',
    PURCHASE_HAS_SUPPLIER_PAYMENTS: 'ZAD.PURCHASES.CANCEL_BLOCK_PAYMENTS',
    PURCHASE_STOCK_INSUFFICIENT: 'ZAD.PURCHASES.CANCEL_BLOCK_STOCK',
    PURCHASE_STOCK_MOVEMENT_INCONSISTENT: 'ZAD.PURCHASES.CANCEL_BLOCK_MOVEMENTS',
    PURCHASE_COST_RESTORATION_UNSAFE: 'ZAD.PURCHASES.CANCEL_BLOCK_COST',
    PRODUCT_HAS_DEPENDENCIES: 'ZAD.PURCHASES.CANCEL_BLOCK_DEPENDENCIES',
    PURCHASE_NOT_CANCELLABLE: 'ZAD.PURCHASES.CANCEL_BLOCK_NOT_CANCELLABLE',
    PURCHASE_ALREADY_CANCELLED: 'ZAD.PURCHASES.CANCEL_BLOCK_ALREADY',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
    CANCELLATION_OPERATION_CONFLICT: 'ZAD.PURCHASES.CANCEL_OP_CONFLICT',
    PURCHASE_FORCE_CANCEL_REQUIRED: 'ZAD.PURCHASES.FORCE_REQUIRED',
    PURCHASE_UNVERIFIED_STOCK_CONFIRMATION_REQUIRED: 'ZAD.PURCHASES.FORCE_UNVERIFIED_REQUIRED',
  }
  return map[String(code || '')] || 'ZAD.PURCHASES.CANCEL_BLOCK_GENERIC'
}

export function zadPurchaseKeepReasonMessageId(reason?: string | null): string {
  const map: Record<string, string> = {
    ARCHIVE_PROVENANCE_UNKNOWN: 'ZAD.PURCHASES.KEEP_PROVENANCE_UNKNOWN',
    OTHER_PURCHASE: 'ZAD.PURCHASES.KEEP_OTHER_PURCHASE',
    HAS_SALES: 'ZAD.PURCHASES.KEEP_HAS_SALES',
    HAS_RETURNS: 'ZAD.PURCHASES.KEEP_HAS_RETURNS',
    HAS_DEPENDENCIES: 'ZAD.PURCHASES.KEEP_DEPENDENCIES',
    STOCK_NOT_ZERO: 'ZAD.PURCHASES.KEEP_STOCK',
    KEEP_EXISTING: 'ZAD.PURCHASES.KEEP_EXISTING',
    SUPPLIER_PAYMENTS: 'ZAD.PURCHASES.KEEP_PAYMENTS',
    STOCK_RECONCILIATION_PENDING: 'ZAD.PURCHASES.KEEP_STOCK_PENDING',
    STOCK_ATTRIBUTION_UNVERIFIED: 'ZAD.PURCHASES.KEEP_STOCK_UNVERIFIED',
  }
  return map[String(reason || '')] || 'ZAD.PURCHASES.KEEP_EXISTING'
}

export function zadProductRestoreMessageId(code?: string | null): string {
  const map: Record<string, string> = {
    PRODUCT_RESTORE_CONFLICT: 'ZAD.PRODUCTS.RESTORE_CONFLICT',
    PRODUCT_ALREADY_ARCHIVED: 'ZAD.PRODUCTS.RESTORE_NOT_ARCHIVED',
    PRODUCT_HAS_DEPENDENCIES: 'ZAD.PRODUCTS.RESTORE_DEPENDENCIES',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
  }
  return map[String(code || '')] || ''
}

export function zadStockAttributionMessageId(status?: string | null): string {
  const map: Record<string, string> = {
    VERIFIED: 'ZAD.PURCHASES.ATTR_VERIFIED',
    AMBIGUOUS: 'ZAD.PURCHASES.ATTR_AMBIGUOUS',
    MISSING_EVIDENCE: 'ZAD.PURCHASES.ATTR_MISSING',
    INCONSISTENT: 'ZAD.PURCHASES.ATTR_INCONSISTENT',
  }
  return map[String(status || '')] || 'ZAD.PURCHASES.ATTR_UNKNOWN'
}

export function zadReconciliationStatusMessageId(status?: string | null): string {
  const map: Record<string, string> = {
    PENDING: 'ZAD.PURCHASES.RECON_PENDING',
    RECONCILED: 'ZAD.PURCHASES.RECON_DONE',
    NONE: 'ZAD.PURCHASES.RECON_NONE',
    NOT_APPLICABLE: 'ZAD.PURCHASES.RECON_NA',
  }
  return map[String(status || '')] || 'ZAD.PURCHASES.RECON_UNKNOWN'
}

export function zadCancellationModeMessageId(mode?: string | null): string {
  if (String(mode || '').toUpperCase() === 'FORCE') return 'ZAD.PURCHASES.MODE_FORCE'
  return 'ZAD.PURCHASES.MODE_NORMAL'
}

export function resolveZadValidatedPurchaseCancelMessage(err: ZadApiError): {
  i18nId: string
  fallback: string
} {
  if (err.status === 404 || err.status === 405) {
    return {i18nId: 'ZAD.PURCHASES.CANCEL_API_UNAVAILABLE', fallback: err.error}
  }
  if (err.status === 503 || err.errorCode === 'PURCHASE_NOT_CANCELLABLE') {
    if (err.status === 503) {
      return {i18nId: 'ZAD.PURCHASES.CANCEL_TX_UNAVAILABLE', fallback: err.error}
    }
  }
  const mapped = zadPurchaseCancelBlockerMessageId(err.errorCode)
  if (mapped !== 'ZAD.PURCHASES.CANCEL_BLOCK_GENERIC' || err.errorCode) {
    return {i18nId: mapped, fallback: err.error}
  }
  return {i18nId: mapped, fallback: err.error}
}

export function mergePurchaseLineIntoReversal(
  row: ZadPurchaseStockReversalRow,
  items: ZadPurchaseItem[] | undefined
): ZadPurchaseStockReversalRow {
  const line = (items || []).find((item) => {
    const id = typeof item.product === 'string' ? item.product : item.product?._id
    return id === row.productId
  })
  if (!line) return row
  return {
    ...row,
    purchaseQuantity: line.quantity,
    purchaseUnit: line.purchaseUnit,
    unitsPerPackageSnapshot: line.unitsPerPackageSnapshot ?? line.unitsPerPackage,
  }
}

export function formatZadPurchaseReversalQty(row: ZadPurchaseStockReversalRow): {
  operatorQty: number | null
  unit: ZadPurchaseUnit
  packets: number | null
  unitsPerPackage: number | null
  baseUnits: number
} {
  const base = Number(row.quantityBaseUnits) || 0
  const unit = normalizeZadPurchaseUnit(row.purchaseUnit)
  const upp = Number(row.unitsPerPackageSnapshot)
  if (unit === 'PACKAGE' && Number.isInteger(upp) && upp >= 2) {
    const packets =
      row.purchaseQuantity != null && Number.isFinite(Number(row.purchaseQuantity))
        ? Number(row.purchaseQuantity)
        : base / upp
    return {
      operatorQty: packets,
      unit: 'PACKAGE',
      packets,
      unitsPerPackage: upp,
      baseUnits: base,
    }
  }
  return {
    operatorQty: row.purchaseQuantity != null ? Number(row.purchaseQuantity) : base,
    unit: 'UNIT',
    packets: null,
    unitsPerPackage: null,
    baseUnits: base,
  }
}

export function isZadPurchaseCancelReasonValid(reason: string): boolean {
  const trimmed = String(reason || '').trim()
  return trimmed.length >= 3 && trimmed.length <= 500
}

export function attachZadProductImportOrigin<T>(
  payload: {products: T[]},
  originPurchase?: string | null
): {products: T[]; originPurchase?: string} {
  const products = Array.isArray(payload?.products) ? payload.products : []
  const origin = String(originPurchase || '').trim()
  if (!origin) return {products}
  return {products, originPurchase: origin}
}

export function isZadDraftPurchaseOrigin(purchase?: Pick<ZadPurchase, 'status'> | null): boolean {
  return purchase?.status === 'DRAFT'
}

export function readZadPurchaseCancellationPreview(raw: unknown): ZadPurchaseCancellationPreview {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const nested = root.preview && typeof root.preview === 'object' ? (root.preview as Record<string, unknown>) : null
  const source = nested || root
  const purchase = source.purchase && typeof source.purchase === 'object' ? (source.purchase as ZadPurchase) : undefined
  return {
    purchaseId: String(source.purchaseId || purchase?._id || ''),
    purchaseNumber: String(source.purchaseNumber || purchase?.purchaseNumber || ''),
    status: String(source.status || purchase?.status || ''),
    purchaseDate: String(source.purchaseDate || purchase?.purchaseDate || ''),
    supplier: (source.supplier as ZadPurchase['supplier']) || purchase?.supplier,
    lineCount:
      typeof source.lineCount === 'number'
        ? source.lineCount
        : Array.isArray(purchase?.items)
          ? (purchase?.items || []).length
          : undefined,
    total: typeof source.total === 'number' ? source.total : purchase?.total,
    canCancel: source.canCancel === true,
    canForceCancel: source.canForceCancel === true,
    force: source.force && typeof source.force === 'object' ? (source.force as ZadPurchaseForcePreview) : undefined,
    stockReversal: Array.isArray(source.stockReversal)
      ? (source.stockReversal as ZadPurchaseStockReversalRow[])
      : [],
    financialConsequences:
      source.financialConsequences && typeof source.financialConsequences === 'object'
        ? (source.financialConsequences as ZadPurchaseFinancialConsequences)
        : undefined,
    reconciliationRequirements:
      source.reconciliationRequirements && typeof source.reconciliationRequirements === 'object'
        ? (source.reconciliationRequirements as ZadPurchaseReconciliationRequirements)
        : undefined,
    linkedSupplierPayments:
      typeof source.linkedSupplierPayments === 'number' ? source.linkedSupplierPayments : undefined,
    productsToArchive: Array.isArray(source.productsToArchive)
      ? (source.productsToArchive as ZadPurchaseArchiveCandidate[])
      : [],
    productsToKeep: Array.isArray(source.productsToKeep)
      ? (source.productsToKeep as ZadPurchaseArchiveCandidate[])
      : [],
    blockers: Array.isArray(source.blockers) ? (source.blockers as ZadPurchaseCancelBlocker[]) : [],
    linkedSales: Array.isArray(source.linkedSales) ? (source.linkedSales as string[]) : [],
    linkedReturns: Array.isArray(source.linkedReturns) ? (source.linkedReturns as string[]) : [],
  }
}

export function readZadPurchaseReconciliation(raw: unknown): ZadPurchaseReconciliationRow | null {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const nested =
    (root.reconciliation && typeof root.reconciliation === 'object'
      ? (root.reconciliation as Record<string, unknown>)
      : null) ||
    (root.purchase && typeof root.purchase === 'object' ? (root.purchase as Record<string, unknown>) : null)
  const source = nested || root
  const cancellation =
    source.cancellation && typeof source.cancellation === 'object'
      ? (source.cancellation as Record<string, unknown>)
      : {}
  const purchaseId = String(source.purchaseId || source._id || root.purchaseId || '')
  if (!purchaseId) return null
  return {
    purchaseId,
    purchaseNumber: String(source.purchaseNumber || ''),
    status: String(source.status || ''),
    supplier: (source.supplier as ZadPurchase['supplier']) || undefined,
    cancelledAt: (cancellation.cancelledAt as string) || (source.cancelledAt as string) || null,
    cancelledBy: cancellation.cancelledBy || source.cancelledBy,
    operationId: (cancellation.operationId as string) || (source.operationId as string) || null,
    mode: (cancellation.mode as string) || (source.mode as string) || null,
    confirmForce: cancellation.confirmForce === true || source.confirmForce === true,
    confirmUnverifiedStock:
      cancellation.confirmUnverifiedStock === true || source.confirmUnverifiedStock === true,
    stockReconciliationStatus:
      (cancellation.stockReconciliationStatus as string) ||
      (source.stockReconciliationStatus as string) ||
      null,
    financialReconciliationStatus:
      (cancellation.financialReconciliationStatus as string) ||
      (source.financialReconciliationStatus as string) ||
      null,
    costReconciliationStatus:
      (cancellation.costReconciliationStatus as string) ||
      (source.costReconciliationStatus as string) ||
      null,
    reconciliation: cancellation.reconciliation ?? source.reconciliation,
    reason: (cancellation.reason as string) || (source.reason as string) || null,
    total: typeof source.total === 'number' ? source.total : undefined,
  }
}

export function readZadPurchaseReconciliationSummary(raw: unknown): ZadPurchaseReconciliationRow[] {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(root.items)
      ? root.items
      : Array.isArray(root.purchases)
        ? root.purchases
        : Array.isArray(root.summary)
          ? root.summary
          : Array.isArray(root.rows)
            ? root.rows
            : []
  return list
    .map((item) => readZadPurchaseReconciliation(item))
    .filter((item): item is ZadPurchaseReconciliationRow => Boolean(item))
}
